import { Component, OnInit } from '@angular/core';

interface ReportItem {
  /** Identificador estable para tracking/búsqueda. */
  key: string;
  /** Título visible. */
  name: string;
  /** Descripción corta (1-2 líneas) para el card. */
  description: string;
  /** Ruta destino. */
  route: string;
  /** Icono Heroicon (path d=…). */
  iconPath: string;
  /** Categoría a la que pertenece (debe coincidir con ReportCategory.key). */
  category: string;
  /** Tags para búsqueda libre. */
  tags?: string[];
  /** Marca el reporte como recién agregado (badge "Nuevo" en el hub). */
  isNew?: boolean;
}

interface ReportCategory {
  key: string;
  name: string;
  description: string;
  /** Color de acento usado para el icono de la categoría. */
  tone: 'sky' | 'emerald' | 'amber' | 'violet' | 'rose' | 'slate';
  iconPath: string;
}

@Component({
  selector: 'app-reports-hub',
  templateUrl: './reports-hub.component.html',
  styleUrls: ['./reports-hub.component.scss']
})
export class ReportsHubComponent implements OnInit {
  searchTerm: string = '';

  categories: ReportCategory[] = [
    {
      key: 'operations',
      name: 'Operación financiera',
      description: 'Movimientos diarios: ingresos, egresos y conciliación bancaria.',
      tone: 'sky',
      iconPath: 'M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941'
    },
    {
      key: 'academic',
      name: 'Inscripciones & académico',
      description: 'Reportes de proyectos, inscripciones y programas.',
      tone: 'emerald',
      iconPath: 'M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5'
    },
    {
      key: 'portfolio',
      name: 'Cartera & cobranza',
      description: 'Seguimiento de cartera vigente y castigada.',
      tone: 'amber',
      iconPath: 'M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z'
    },
    {
      key: 'gateway',
      name: 'Pasarela de pagos',
      description: 'Reportes de Wompi: pagos, consolidados y links.',
      tone: 'violet',
      iconPath: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z'
    },
    {
      key: 'compliance',
      name: 'Cumplimiento & exógenas',
      description: 'Reportes para DIAN y auditoría externa.',
      tone: 'slate',
      iconPath: 'M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z'
    }
  ];

  reports: ReportItem[] = [
    // Operación financiera
    {
      key: 'income-receipts',
      name: 'Comprobantes de Ingreso',
      description: 'Listado y análisis de comprobantes de ingreso emitidos.',
      route: '/reports/income-receipts',
      iconPath: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z',
      category: 'operations',
      tags: ['ingresos', 'cobros', 'pagos recibidos', 'recibos']
    },
    {
      key: 'expense-receipts',
      name: 'Comprobantes de Egreso',
      description: 'Transacciones salientes y pagos a terceros.',
      route: '/reports/expense-receipts',
      iconPath: 'M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941',
      category: 'operations',
      tags: ['egresos', 'salidas', 'pagos', 'gastos']
    },
    {
      key: 'work-orders',
      name: 'Órdenes de Trabajo',
      description: 'Estado y seguimiento de OTs emitidas y pagadas.',
      route: '/reports/work-orders',
      iconPath: 'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z',
      category: 'operations',
      tags: ['ot', 'órdenes', 'trabajo']
    },
    {
      key: 'banks',
      name: 'Reportes Bancarios',
      description: 'Movimientos y consolidados por cuenta bancaria.',
      route: '/reports/banks',
      iconPath: 'M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75Z',
      category: 'operations',
      tags: ['bancos', 'cuentas', 'movimientos']
    },
    // Académico
    {
      key: 'project-enrollments',
      name: 'Inscripciones por Proyecto',
      description: 'Distribución de inscritos y estados por proyecto.',
      route: '/reports/project-enrollments',
      iconPath: 'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
      category: 'academic',
      tags: ['inscripciones', 'proyecto', 'estudiantes']
    },
    {
      key: 'project-program-detail',
      name: 'Detalle de Programa',
      description: 'Cohortes, módulos y desempeño por programa.',
      route: '/reports/project-program-detail',
      iconPath: 'M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25',
      category: 'academic',
      tags: ['programa', 'detalle', 'cohorte', 'módulos']
    },
    {
      key: 'project-budget-execution',
      name: 'Ejecución Presupuestal',
      description: 'Presupuesto vs ejecutado por proyecto.',
      route: '/reports/project-budget-execution',
      iconPath: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z',
      category: 'academic',
      tags: ['presupuesto', 'ejecución', 'budget']
    },
    {
      key: 'created-projects',
      name: 'Proyectos Creados',
      description: 'Listado de proyectos por rango de creación, con filtro opcional por proyecto.',
      route: '/reports/created-projects',
      iconPath: 'M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z',
      category: 'academic',
      tags: ['proyectos', 'creación', 'cierre', 'estados'],
      isNew: true
    },
    {
      key: 'project-financial-statement',
      name: 'Estado Financiero del Proyecto (PDF)',
      description: 'Resumen ejecutivo del proyecto: presupuestado vs ejecutado, utilidad y ajustes.',
      route: '/reports/project-financial-statement',
      iconPath: 'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z',
      category: 'academic',
      tags: ['estado financiero', 'PDF', 'utilidad', 'cierre'],
      isNew: true
    },

    // Cumplimiento & exógenas
    {
      key: 'registered-data',
      name: 'Datos Registrados',
      description: 'Proveedores, empresas y estudiantes en un solo Excel (3 hojas).',
      route: '/reports/registered-data',
      iconPath: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z',
      category: 'compliance',
      tags: ['DIAN', 'exógena', 'maestros', 'datos'],
      isNew: true
    },
    {
      key: 'participants-by-incp',
      name: 'Participantes por INCP',
      description: 'Repitencia y universo de participantes por inscripción (individuales + corporativas).',
      route: '/reports/participants-by-incp',
      iconPath: 'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
      category: 'compliance',
      tags: ['DIAN', 'repitencia', 'inscripciones', 'participantes'],
      isNew: true
    },

    // Cartera
    {
      key: 'portfolio',
      name: 'Reporte de Cartera',
      description: 'Análisis integral de cartera vigente.',
      route: '/reports/portfolio',
      iconPath: 'M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z',
      category: 'portfolio',
      tags: ['cartera', 'cobros', 'mora']
    },
    {
      key: 'punished-portfolio',
      name: 'Cartera Castigada',
      description: 'Movimientos y saldos de cartera castigada.',
      route: '/reports/punished-portfolio',
      iconPath: 'M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z',
      category: 'portfolio',
      tags: ['castigada', 'cartera', 'incobrable']
    },

    // Wompi
    {
      key: 'wompi-consolidated',
      name: 'Consolidado Wompi',
      description: 'Reporte consolidado de transacciones por Wompi.',
      route: '/reports/wompi-consolidated',
      iconPath: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z',
      category: 'gateway',
      tags: ['wompi', 'consolidado', 'pasarela']
    },
    {
      key: 'wompi-payments',
      name: 'Pagos Wompi',
      description: 'Detalle de pagos individuales procesados por Wompi.',
      route: '/reports/wompi-payments',
      iconPath: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z',
      category: 'gateway',
      tags: ['wompi', 'pagos', 'transacciones']
    },
    {
      key: 'payment-links-audit',
      name: 'Links de Pago',
      description: 'Auditoría de links de pago generados y su estado.',
      route: '/reports/payment-links-audit',
      iconPath: 'M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244',
      category: 'gateway',
      tags: ['links', 'pago', 'wompi', 'auditoría']
    }
  ];

  /** Reportes destacados (más usados). */
  get popularReports(): ReportItem[] {
    const popularKeys = ['income-receipts', 'expense-receipts', 'portfolio', 'wompi-consolidated'];
    return popularKeys
      .map(k => this.reports.find(r => r.key === k))
      .filter((r): r is ReportItem => !!r);
  }

  /** Reportes filtrados por búsqueda. Si no hay término, devuelve todos. */
  get filteredReports(): ReportItem[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.reports;
    return this.reports.filter(r => {
      const haystack = [r.name, r.description, ...(r.tags || [])].join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }

  /** Devuelve los reportes que pertenecen a una categoría (filtrados). */
  reportsByCategory(categoryKey: string): ReportItem[] {
    return this.filteredReports.filter(r => r.category === categoryKey);
  }

  /** Devuelve categorías que tienen al menos un reporte coincidente con el filtro. */
  get visibleCategories(): ReportCategory[] {
    return this.categories.filter(c => this.reportsByCategory(c.key).length > 0);
  }

  ngOnInit(): void {}

  clearSearch(): void {
    this.searchTerm = '';
  }
}
